// js/tradeControlAI.js
// Motor de Análisis IA para Trade Control

class TradeControlAI {
    constructor() {
        this.analysisCache = null;
        this.cacheExpiry = null;
    }

    /**
     * Ejecuta el análisis completo con IA
     */
    async runAnalysis() {
        try {
            // Verificar cache (válido por 24 horas)
            if (this.isCacheValid()) {
                return this.analysisCache;
            }

            const accountId = window.currentAccount;
            const userId = window.currentUserId || sessionStorage.getItem('currentUserId') || localStorage.getItem('currentUserId');
            
            if (!accountId || !userId) {
                throw new Error('No hay cuenta o usuario seleccionado');
            }

            // Ejecutar análisis en paralelo
            const [
                bestPair,
                worstPair,
                dayAnalysis,
                timeAnalysis,
                streakAnalysis,
                keyInsights
            ] = await Promise.all([
                this.getBestPair(accountId),
                this.getWorstPair(accountId),
                this.getDayOfWeekAnalysis(accountId),
                this.getTimeAnalysis(accountId),
                this.getStreakAnalysis(accountId),
                this.getKeyInsights(accountId)
            ]);

            // Calcular recomendación global y confianza
            const { recommendation, confidence, message } = this.calculateGlobalRecommendation({
                bestPair,
                dayAnalysis,
                timeAnalysis,
                streakAnalysis
            });

            const analysis = {
                recommendation,
                confidence,
                message,
                bestPair,
                worstPair,
                dayAnalysis,
                timeAnalysis,
                streakAnalysis,
                keyInsights,
                timestamp: Date.now()
            };

            // Guardar en cache
            this.cacheAnalysis(analysis);

            return analysis;

        } catch (error) {

            return this.getFallbackAnalysis();
        }
    }

    /**
     * Obtiene el mejor par para operar hoy
     */
    async getBestPair(accountId) {
        try {
            const turso = window.tursoClient;
            if (!turso) throw new Error('No hay conexión con la base de datos');

            const today = new Date();
            const dayOfWeek = today.getDay();

            const result = await turso.execute({
                sql: `
                    SELECT 
                        pair as symbol,
                        COUNT(*) as total_trades,
                        AVG(profit_usd) as avg_profit,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as win_rate
                    FROM trades
                    WHERE account_id = ?
                        AND CAST(strftime('%w', date) AS INTEGER) = ?
                        AND date >= date('now', '-90 days')
                    GROUP BY pair
                    HAVING total_trades >= 3
                    ORDER BY win_rate DESC, avg_profit DESC
                    LIMIT 1
                `,
                args: [accountId, dayOfWeek]
            });

            if (!result || !result.rows || result.rows.length === 0) {
                return {
                    name: 'EURUSD',
                    winRate: 0,
                    avgProfit: 0,
                    trades: 0,
                    reason: 'No hay suficiente historial para este día'
                };
            }

            const row = result.rows[0];
            const dayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
            
            return {
                name: row.symbol,
                winRate: Math.round(parseFloat(row.win_rate) || 0),
                avgProfit: Math.round(parseFloat(row.avg_profit) || 0),
                trades: parseInt(row.total_trades) || 0,
                reason: `Históricamente tu mejor par los ${dayNames[dayOfWeek]} entre 09:00-11:00`
            };

        } catch (error) {

            return {
                name: 'EURUSD',
                winRate: 0,
                avgProfit: 0,
                trades: 0,
                reason: 'Error al obtener datos'
            };
        }
    }

    /**
     * Obtiene el peor par (a evitar)
     */
    async getWorstPair(accountId) {
        try {
            const turso = window.tursoClient;
            if (!turso) throw new Error('No hay conexión con la base de datos');

            const today = new Date();
            const dayOfWeek = today.getDay();

            const result = await turso.execute({
                sql: `
                    SELECT 
                        pair as symbol,
                        COUNT(*) as total_trades,
                        AVG(profit_usd) as avg_loss,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as win_rate
                    FROM trades
                    WHERE account_id = ?
                        AND CAST(strftime('%w', date) AS INTEGER) = ?
                        AND date >= date('now', '-90 days')
                    GROUP BY pair
                    HAVING total_trades >= 3
                    ORDER BY win_rate ASC, avg_loss ASC
                    LIMIT 1
                `,
                args: [accountId, dayOfWeek]
            });

            if (!result || !result.rows || result.rows.length === 0) {
                return {
                    name: 'N/A',
                    winRate: 0,
                    avgLoss: 0,
                    reason: 'No hay suficiente historial'
                };
            }

            const row = result.rows[0];
            const dayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
            
            return {
                name: row.symbol,
                winRate: Math.round(parseFloat(row.win_rate) || 0),
                avgLoss: Math.round(parseFloat(row.avg_loss) || 0),
                reason: `Bajo rendimiento los ${dayNames[dayOfWeek]}`
            };

        } catch (error) {

            return {
                name: 'N/A',
                winRate: 0,
                avgLoss: 0,
                reason: 'Error al obtener datos'
            };
        }
    }

    /**
     * Analiza el día de la semana actual
     */
    async getDayOfWeekAnalysis(accountId) {
        try {
            const turso = window.tursoClient;
            if (!turso) throw new Error('No hay conexión con la base de datos');

            const today = new Date();
            const dayOfWeek = today.getDay();
            const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

            // WinRate del día actual (últimos 3 meses)
            const dayResult = await turso.execute({
                sql: `
                    SELECT 
                        COUNT(*) as trades,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND CAST(strftime('%w', date) AS INTEGER) = ?
                        AND date >= date('now', '-90 days')
                `,
                args: [accountId, dayOfWeek]
            });

            // WinRate promedio general
            const avgResult = await turso.execute({
                sql: `
                    SELECT 
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as avg_wr
                    FROM trades
                    WHERE account_id = ?
                        AND date >= date('now', '-90 days')
                `,
                args: [accountId]
            });

            const tradesAnalyzed = parseInt(dayResult?.rows?.[0]?.trades) || 0;
            const historicalWR = Math.round(parseFloat(dayResult?.rows?.[0]?.wr) || 0);
            const avgWR = Math.round(parseFloat(avgResult?.rows?.[0]?.avg_wr) || 0);

            const diff = historicalWR - avgWR;
            let status = 'average';
            if (diff >= 5) status = 'above_average';
            else if (diff <= -5) status = 'below_average';

            return {
                dayOfWeek: dayNames[dayOfWeek],
                historicalWR,
                avgWR,
                status,
                tradesAnalyzed
            };

        } catch (error) {

            return {
                dayOfWeek: 'N/A',
                historicalWR: 0,
                avgWR: 0,
                status: 'average',
                tradesAnalyzed: 0
            };
        }
    }

    /**
     * Analiza el horario óptimo para operar
     */
    async getTimeAnalysis(accountId) {
        try {
            const turso = window.tursoClient;
            if (!turso) throw new Error('No hay conexión con la base de datos');

            const result = await turso.execute({
                sql: `
                    SELECT 
                        CASE 
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 9 AND 10 THEN '09:00-11:00'
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 11 AND 13 THEN '11:00-13:00'
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 13 AND 15 THEN '13:00-15:00'
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 15 AND 17 THEN '15:00-17:00'
                            ELSE 'Otros'
                        END as hour_range,
                        COUNT(*) as trades,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND date >= date('now', '-90 days')
                        AND entry_time IS NOT NULL
                    GROUP BY hour_range
                    HAVING trades >= 5
                    ORDER BY wr DESC
                    LIMIT 1
                `,
                args: [accountId]
            });

            const now = new Date();
            const currentHour = now.getHours();
            const currentTime = `${String(currentHour).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

            if (!result || !result.rows || result.rows.length === 0) {
                return {
                    bestHour: '09:00-11:00',
                    currentTime,
                    isOptimal: currentHour >= 9 && currentHour < 11,
                    hourWR: 0
                };
            }

            const row = result.rows[0];
            const bestHour = row.hour_range;
            const hourWR = Math.round(parseFloat(row.wr) || 0);

            // Verificar si estamos en horario óptimo
            let isOptimal = false;
            if (bestHour === '09:00-11:00') isOptimal = currentHour >= 9 && currentHour < 11;
            else if (bestHour === '11:00-13:00') isOptimal = currentHour >= 11 && currentHour < 13;
            else if (bestHour === '13:00-15:00') isOptimal = currentHour >= 13 && currentHour < 15;
            else if (bestHour === '15:00-17:00') isOptimal = currentHour >= 15 && currentHour < 17;

            return {
                bestHour,
                currentTime,
                isOptimal,
                hourWR
            };

        } catch (error) {

            const now = new Date();
            return {
                bestHour: '09:00-11:00',
                currentTime: `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`,
                isOptimal: false,
                hourWR: 0
            };
        }
    }

    /**
     * Analiza la racha actual
     */
    async getStreakAnalysis(accountId) {
        try {
            const turso = window.tursoClient;
            if (!turso) throw new Error('No hay conexión con la base de datos');

            // Obtener últimos 5 trades
            const result = await turso.execute({
                sql: `
                    SELECT profit_usd, id
                    FROM trades
                    WHERE account_id = ?
                    ORDER BY id DESC
                    LIMIT 5
                `,
                args: [accountId]
            });

            if (!result || !result.rows || result.rows.length === 0) {
                return {
                    current: 'NEUTRAL',
                    consecutive: 0,
                    pattern: 'No hay suficiente historial para detectar patrones',
                    recommendation: 'Opera con disciplina y registra todos tus trades'
                };
            }

            // Calcular racha
            let consecutive = 0;
            let current = null;

            for (const trade of result.rows) {
                const profit = parseFloat(trade.profit_usd);
                const isWin = profit > 0;

                if (current === null) {
                    current = isWin ? 'WIN' : 'LOSS';
                    consecutive = 1;
                } else if ((current === 'WIN' && isWin) || (current === 'LOSS' && !isWin)) {
                    consecutive++;
                } else {
                    break;
                }
            }

            // Obtener patrón histórico
            const patternResult = await turso.execute({
                sql: `
                    SELECT 
                        COUNT(*) as total,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND id > (
                            SELECT MAX(t2.id)
                            FROM trades t2
                            WHERE t2.account_id = ?
                                AND t2.profit_usd > 0
                                AND t2.id < trades.id
                        )
                `,
                args: [accountId, accountId]
            });

            let pattern = '';
            let recommendation = '';

            if (current === 'WIN') {
                const afterWinsWR = patternResult?.rows?.[0]?.wr ? Math.round(parseFloat(patternResult.rows[0].wr)) : 50;
                pattern = `Después de ${consecutive} ${consecutive === 1 ? 'victoria' : 'victorias'}, tu siguiente trade tiene ${afterWinsWR}% de éxito`;
                recommendation = consecutive >= 2 ? 
                    'Momentum positivo - Mantén disciplina y no sobreoperes' : 
                    'Mantén tu plan de trading';
            } else {
                pattern = `Racha de ${consecutive} ${consecutive === 1 ? 'pérdida' : 'pérdidas'} consecutiva${consecutive === 1 ? '' : 's'}`;
                recommendation = consecutive >= 2 ? 
                    '⚠️ Considera tomar un descanso o reducir el riesgo' : 
                    'Analiza tu último trade y ajusta si es necesario';
            }

            return {
                current,
                consecutive,
                pattern,
                recommendation
            };

        } catch (error) {

            return {
                current: 'NEUTRAL',
                consecutive: 0,
                pattern: 'Error al obtener datos',
                recommendation: 'Opera con disciplina'
            };
        }
    }

    /**
     * Genera key insights basados en el historial
     */
    async getKeyInsights(accountId) {
        try {
            const turso = window.tursoClient;
            if (!turso) throw new Error('No hay conexión con la base de datos');

            const insights = [];

            // Insight 1: Mejor setup completo (día + hora + par)
            const bestSetupResult = await turso.execute({
                sql: `
                    SELECT 
                        CAST(strftime('%w', date) AS INTEGER) as day,
                        CAST(strftime('%H', entry_time) AS INTEGER) as hour,
                        pair as symbol,
                        COUNT(*) as trades,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND date >= date('now', '-90 days')
                        AND entry_time IS NOT NULL
                    GROUP BY day, hour, pair
                    HAVING trades >= 3
                    ORDER BY wr DESC
                    LIMIT 1
                `,
                args: [accountId]
            });

            if (bestSetupResult?.rows?.[0]) {
                const row = bestSetupResult.rows[0];
                const dayNames = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
                const day = dayNames[parseInt(row.day)];
                const hour = parseInt(row.hour);
                const symbol = row.symbol;
                const wr = Math.round(parseFloat(row.wr));
                
                insights.push(`Tu mejor setup es ${day} ${hour}:00-${hour+2}:00 con ${symbol} (${wr}% WR)`);
            }

            // Insight 2: Horario a evitar
            const worstHourResult = await turso.execute({
                sql: `
                    SELECT 
                        CAST(strftime('%H', entry_time) AS INTEGER) as hour,
                        COUNT(*) as trades,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND date >= date('now', '-90 days')
                        AND entry_time IS NOT NULL
                    GROUP BY hour
                    HAVING trades >= 5
                    ORDER BY wr ASC
                    LIMIT 1
                `,
                args: [accountId]
            });

            if (worstHourResult?.rows?.[0]) {
                const row = worstHourResult.rows[0];
                const hour = parseInt(row.hour);
                const wr = Math.round(parseFloat(row.wr));
                
                if (wr < 50) {
                    insights.push(`Evita operar después de las ${hour}:00 (WR: ${wr}%)`);
                }
            }

            // Insight 3: Análisis de stop-loss óptimo (comentado - columna stop_loss no existe en schema)
            // const slAnalysisResult = await turso.execute({
            //     sql: `
            //         SELECT 
            //             CASE 
            //                 WHEN ABS(entry_price - exit_price) * 10000 < 30 THEN '< 30 pips'
            //                 WHEN ABS(entry_price - exit_price) * 10000 BETWEEN 30 AND 50 THEN '30-50 pips'
            //                 WHEN ABS(entry_price - exit_price) * 10000 BETWEEN 50 AND 70 THEN '50-70 pips'
            //                 ELSE '> 70 pips'
            //             END as price_range,
            //             COUNT(*) as trades,
            //             SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
            //         FROM trades
            //         WHERE account_id = ?
            //             AND entry_price IS NOT NULL 
            //             AND exit_price IS NOT NULL
            //             AND date >= date('now', '-90 days')
            //         GROUP BY price_range
            //         HAVING trades >= 3
            //         ORDER BY wr DESC
            //     `,
            //     args: [accountId]
            // });

            // if (slAnalysisResult?.rows && slAnalysisResult.rows.length >= 2) {
            //     const best = slAnalysisResult.rows[0];
            //     const worst = slAnalysisResult.rows[slAnalysisResult.rows.length - 1];
            //     const bestWR = Math.round(parseFloat(best.wr));
            //     const worstWR = Math.round(parseFloat(worst.wr));
            //     
            //     insights.push(`Tus movimientos en ${best.price_range} tienen ${bestWR}% éxito vs ${worst.price_range} (${worstWR}%)`);
            // }

            // Insight 4: Análisis de sesión de trading
            const sessionResult = await turso.execute({
                sql: `
                    SELECT 
                        CASE 
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 8 AND 12 THEN 'Sesión Europea'
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 13 AND 17 THEN 'Sesión Americana'
                            WHEN CAST(strftime('%H', entry_time) AS INTEGER) BETWEEN 0 AND 7 THEN 'Sesión Asiática'
                            ELSE 'Otras horas'
                        END as session,
                        COUNT(*) as trades,
                        SUM(CASE WHEN profit_usd > 0 THEN 1 ELSE 0 END) * 100.0 / COUNT(*) as wr
                    FROM trades
                    WHERE account_id = ?
                        AND date >= date('now', '-90 days')
                        AND entry_time IS NOT NULL
                    GROUP BY session
                    HAVING trades >= 5
                    ORDER BY wr DESC
                    LIMIT 1
                `,
                args: [accountId]
            });

            if (sessionResult?.rows?.[0]) {
                const row = sessionResult.rows[0];
                const session = row.session;
                const wr = Math.round(parseFloat(row.wr));
                
                insights.push(`Tu mejor rendimiento es en ${session} (${wr}% WR)`);
            }

            // Insight 5: Trades consecutivos
            const consecutiveResult = await turso.execute({
                sql: `
                    SELECT 
                        COUNT(DISTINCT date) as days_with_3plus
                    FROM (
                        SELECT 
                            date,
                            COUNT(*) as daily_trades
                        FROM trades
                        WHERE account_id = ?
                            AND date >= date('now', '-30 days')
                        GROUP BY date
                        HAVING daily_trades >= 3
                    )
                `,
                args: [accountId]
            });

            if (consecutiveResult?.rows?.[0]) {
                const daysOver3 = parseInt(consecutiveResult.rows[0].days_with_3plus) || 0;
                const totalDays = 30;
                const percentage = Math.round((daysOver3 / totalDays) * 100);
                
                if (percentage > 50) {
                    insights.push(`⚠️ Operas 3+ trades en ${percentage}% de días - Considera calidad sobre cantidad`);
                }
            }

            // Si no hay insights, devolver mensajes por defecto
            if (insights.length === 0) {
                insights.push('Necesitas más historial para generar insights personalizados');
                insights.push('Registra al menos 20 trades para obtener análisis detallados');
                insights.push('Mantén consistencia en tu estrategia de trading');
            }

            return insights;

        } catch (error) {

            return [
                'Error al obtener insights',
                'Asegúrate de tener suficiente historial de trades',
                'Registra tus operaciones con todos los detalles'
            ];
        }
    }

    /**
     * Calcula la recomendación global basada en todos los factores
     */
    calculateGlobalRecommendation(data) {
        const { bestPair, dayAnalysis, timeAnalysis, streakAnalysis } = data;

        let score = 0;
        let maxScore = 100;

        // Factor 1: Mejor par (20 puntos)
        if (bestPair.winRate >= 70) score += 20;
        else if (bestPair.winRate >= 60) score += 15;
        else if (bestPair.winRate >= 50) score += 10;
        else score += 5;

        // Factor 2: Día de la semana (30 puntos)
        if (dayAnalysis.status === 'above_average') score += 30;
        else if (dayAnalysis.status === 'average') score += 20;
        else score += 10;

        // Factor 3: Horario (20 puntos)
        if (timeAnalysis.isOptimal) score += 20;
        else score += 10;

        // Factor 4: Racha (30 puntos)
        if (streakAnalysis.current === 'WIN') {
            if (streakAnalysis.consecutive >= 3) score += 25; // No demasiado confiado
            else score += 30;
        } else {
            if (streakAnalysis.consecutive >= 3) score += 5; // Racha negativa fuerte
            else score += 15;
        }

        const confidence = Math.min(100, Math.round(score));

        let recommendation = 'CAUTION';
        let message = 'Condiciones Moderadas - Opera con precaución';

        if (confidence >= 75) {
            recommendation = 'FAVORABLE';
            message = '✅ Condiciones Favorables para Operar';
        } else if (confidence >= 50) {
            recommendation = 'CAUTION';
            message = '⚠️ Condiciones Moderadas - Precaución';
        } else {
            recommendation = 'DANGER';
            message = '🚫 Condiciones Desfavorables - Evita operar';
        }

        return { recommendation, confidence, message };
    }

    /**
     * Verifica si el cache es válido
     */
    isCacheValid() {
        if (!this.analysisCache || !this.cacheExpiry) {
            return false;
        }

        return Date.now() < this.cacheExpiry;
    }

    /**
     * Guarda análisis en cache
     */
    async cacheAnalysis(analysis) {
        this.analysisCache = analysis;
        this.cacheExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 horas

        // También guardar en localStorage
        try {
            const accountId = window.currentAccount;
            const today = new Date().toISOString().split('T')[0];
            
            // Obtener número de trades para invalidar cache cuando cambien
            const turso = window.tursoClient;
            let tradeCount = 0;
            if (turso) {
                const result = await turso.execute({
                    sql: 'SELECT COUNT(*) as count FROM trades WHERE account_id = ?',
                    args: [accountId]
                });
                tradeCount = parseInt(result?.rows?.[0]?.count) || 0;
            }
            
            const key = `TC_AI_ANALYSIS_${accountId}_${today}_${tradeCount}`;
            
            // Limpiar cachés antiguos del día
            this.clearOldCaches(accountId, today, tradeCount);
            
            localStorage.setItem(key, JSON.stringify({
                data: analysis,
                expiry: this.cacheExpiry
            }));
        } catch (e) {

        }
    }

    /**
     * Limpia cachés antiguos del día actual
     */
    clearOldCaches(accountId, today, currentCount) {
        try {
            const prefix = `TC_AI_ANALYSIS_${accountId}_${today}_`;
            const keysToRemove = [];
            
            // Buscar todas las keys de caché del día
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    const count = parseInt(key.split('_').pop());
                    if (count !== currentCount) {
                        keysToRemove.push(key);
                    }
                }
            }
            
            // Eliminar cachés obsoletos
            keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (e) {

        }
    }

    /**
     * Carga análisis desde localStorage
     */
    async loadCachedAnalysis() {
        try {
            const accountId = window.currentAccount;
            const today = new Date().toISOString().split('T')[0];
            
            // Obtener número de trades actual
            const turso = window.tursoClient;
            let tradeCount = 0;
            if (turso) {
                const result = await turso.execute({
                    sql: 'SELECT COUNT(*) as count FROM trades WHERE account_id = ?',
                    args: [accountId]
                });
                tradeCount = parseInt(result?.rows?.[0]?.count) || 0;
            }
            
            const key = `TC_AI_ANALYSIS_${accountId}_${today}_${tradeCount}`;
            
            // Limpiar cachés antiguos de hoy con diferentes counts
            this.clearOldCaches(accountId, today, tradeCount);
            
            const cached = localStorage.getItem(key);
            if (!cached) return null;

            const parsed = JSON.parse(cached);
            if (Date.now() < parsed.expiry) {
                this.analysisCache = parsed.data;
                this.cacheExpiry = parsed.expiry;
                return parsed.data;
            }

            // Expirado, limpiar
            localStorage.removeItem(key);
            return null;

        } catch (e) {

            return null;
        }
    }

    /**
     * Análisis de fallback cuando hay error
     */
    getFallbackAnalysis() {
        return {
            recommendation: 'CAUTION',
            confidence: 50,
            message: '⚠️ No se pudo completar el análisis - Datos insuficientes',
            bestPair: {
                name: 'EURUSD',
                winRate: 0,
                avgProfit: 0,
                trades: 0,
                reason: 'Datos insuficientes'
            },
            worstPair: {
                name: 'N/A',
                winRate: 0,
                avgLoss: 0,
                reason: 'Datos insuficientes'
            },
            dayAnalysis: {
                dayOfWeek: new Date().toLocaleDateString('es-ES', { weekday: 'long' }),
                historicalWR: 0,
                avgWR: 0,
                status: 'average',
                tradesAnalyzed: 0
            },
            timeAnalysis: {
                bestHour: '09:00-11:00',
                currentTime: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                isOptimal: false,
                hourWR: 0
            },
            streakAnalysis: {
                current: 'NEUTRAL',
                consecutive: 0,
                pattern: 'No hay suficiente historial',
                recommendation: 'Opera con disciplina'
            },
            keyInsights: [
                'Necesitas más historial para análisis personalizados',
                'Registra al menos 20 trades para insights detallados',
                'Mantén consistencia en tu estrategia'
            ],
            timestamp: Date.now()
        };
    }

    /**
     * Limpia cache antiguo
     */
    clearOldCache() {
        try {
            const keys = Object.keys(localStorage);
            const prefix = 'TC_AI_ANALYSIS_';
            const today = new Date().toISOString().split('T')[0];

            keys.forEach(key => {
                if (key.startsWith(prefix) && !key.includes(today)) {
                    localStorage.removeItem(key);
                }
            });
        } catch (e) {

        }
    }
}

// Inicializar globalmente
if (typeof window !== 'undefined') {
    window.tradeControlAI = new TradeControlAI();
    
    // Limpiar cache antiguo al inicializar
    window.tradeControlAI.clearOldCache();
}
